// The campaign angle registry: one entry per ad angle we buy traffic for.
//
// ## What an angle is
//
// An ad makes a promise ("Finally fixed my boomy bass"). A visitor who clicks
// it arrives mid-thought. Today they land on a page that starts the
// conversation over, and 95% of them leave: the first paid campaign converted
// 5% of its clicks into a started flow, against 59% for people who arrived
// from a search they typed themselves. The difference is not traffic quality,
// it is that the searcher's own words were still on screen and the ad
// clicker's were not.
//
// An angle carries the ad's promise into the product. Each entry names:
//   - the promise, restated on the first screen the click lands on;
//   - the id every analytics event in that session is tagged with, so the
//     whole funnel is splittable by ad without instrumenting anything twice.
//
// ## An angle never answers a question for the visitor
//
// An earlier version of this used the ad's promise to PREFILL the onboarding
// answers, on the theory that a "fix my bass" click had already told us the
// problem. That was wrong, and deliberately reverted: the answers drive the
// personalisation downstream -- the generate step's glance ordering and
// StepResult's payoff line both read the problem -- so prefilling them builds
// that personalisation on what an ad was CLICKED on rather than on what the
// visitor actually said. A click is an interest, not an answer. The hook
// restates the promise and then everyone answers the same questions.
//
// ## Copy here is real, and is written to CONTINUE its ad
//
// Each entry's copy is the next sentence of the headline that brought the
// visitor, not a restatement of the product. Rewriting it is a single-file
// edit and breaks no test: the tests in campaignAngles.test.ts assert
// STRUCTURE (every angle complete, every prefill a real taxonomy id, ids
// url-safe and stable) and deliberately never assert on wording, so marketing
// can change its mind without anyone editing a test.
//
// ## Adding an angle
//
// Add an entry here. That is the whole change: `CampaignAngleId` derives from
// this object, so the hook screen, the prefill and the analytics all pick it
// up, and the completeness test fails if anything is missing. Then point an ad
// at `/room/new?a=<id>&utm_...` (see docs/campaign-playbook.md for the full
// URL shape).
//
// ## Ids are a wire contract
//
// An id appears in live ad URLs and in every PostHog event of every session
// that clicked one. Renaming one silently splits that ad's history in two.
// Add new ids; retire old ones by leaving them here with `retired: true`.

/** URL parameter an ad uses to select its angle. Short on purpose: it is
 *  typed into an ads manager by hand and shows in the address bar.
 *
 *  Lives HERE rather than next to the resolver so that building or validating
 *  an ad URL needs only this module. `scripts/campaign-urls.ts` runs outside
 *  Next, where the resolver's `@/lib/analytics` import does not resolve. */
export const CAMPAIGN_ANGLE_PARAM = 'a';

export interface CampaignAngle {
  /** Stable, url-safe, and permanent once an ad has used it. */
  id: string;
  /** Internal one-liner: which ad family this serves. Never rendered. */
  intent: string;
  copy: {
    /** The ad's promise, restated. Reads as continuous with what they clicked. */
    headline: string;
    /** One line that pays the promise off with something checkable. */
    subhead: string;
    /** Primary action into the flow. */
    cta: string;
  };
  /** Kept for its history but no longer bought. Resolves like any other angle
   *  for a stale link still in the wild; never offered for a new ad. */
  retired?: boolean;
}

/**
 * One entry per ad headline actually being run. The copy is written to
 * CONTINUE its headline, not to restate the product: a visitor arrives
 * mid-thought and the first screen has to sound like the next sentence of the
 * thing they just read, or they are starting over.
 *
 * Headlines these serve (2026-09-12):
 *
 *   sloped   "Every room calculator assumes a rectangular box.
 *             Mine has a sloped ceiling."
 *   bass     "A $10k speaker upgrade won't fix your hollow bass -
 *             your room will"
 *   theater  "I was quoted $3,000 to have my theater room designed..."
 *
 * `bass` and `theater` keep the ids they had as placeholders: an id is a wire
 * contract and these two are the same angle, re-written. The unused
 * placeholders (`proof`, `placement`, `studio`) are gone rather than retired,
 * because no ad ever pointed at them and so there is no history to keep.
 */
const ANGLES = {
  sloped: {
    id: 'sloped',
    intent:
      'Differentiator: the reader has a room that other calculators cannot '
      + 'model, and has already been failed by one.',
    copy: {
      headline: 'Your room is unique, and so are its acoustics.',
      subhead: 'Generic advice rarely applies to uniquely shaped rooms.',
      cta: 'Build my room',
    },
  },
  bass: {
    id: 'bass',
    intent:
      'Symptom-first, with the gear objection answered: the reader is deciding '
      + 'between spending on speakers and fixing the room.',
    copy: {
      headline: 'Your room is eating your bass.',
      subhead:
        'No speaker can out-run an untreated room. See how to treat yours '
        + 'before you spend anything.',
      cta: 'Plan my room',
    },
  },
  theater: {
    id: 'theater',
    intent:
      'Price-anchored: the reader has been quoted for professional design and '
      + 'is weighing it up.',
    copy: {
      headline: 'You might be able to skip the quote.',
      subhead:
        'A good acoustician simulates your room, lets you hear a before/after, '
        + 'and makes a 3D render. We do that in 5 minutes.',
      cta: 'Simulate my room',
    },
  },
} as const satisfies Record<string, CampaignAngle>;

/** The id union, derived from the object so adding an entry is the whole
 *  change. Read through `CAMPAIGN_ANGLES` below rather than `ANGLES`: the
 *  `as const` that gives us this union also narrows every field to its own
 *  literal, which makes `angle.retired` and an absent `prefill.goal` type
 *  errors at the read site even though both are legal on `CampaignAngle`. */
export type CampaignAngleId = keyof typeof ANGLES;

/** The registry, widened back to the declared shape for reading. */
export const CAMPAIGN_ANGLES: Record<CampaignAngleId, CampaignAngle> = ANGLES;

/** Every angle id, including retired ones. Ordered for stable test output. */
export const CAMPAIGN_ANGLE_IDS = Object.keys(CAMPAIGN_ANGLES).sort() as CampaignAngleId[];

/** The ids an ad may still be pointed at. */
export const LIVE_CAMPAIGN_ANGLE_IDS = CAMPAIGN_ANGLE_IDS.filter(
  (id) => !CAMPAIGN_ANGLES[id].retired,
);

/** Narrow an untrusted string (a URL parameter) to a known angle.
 *
 *  Returns null for anything unrecognised, which is the safe default
 *  everywhere: an unknown `?a=` value must leave the flow exactly as it is
 *  for organic visitors rather than render a blank hook. That also means a
 *  typo in an ad URL degrades to today's behaviour instead of a broken page. */
export function isCampaignAngleId(value: unknown): value is CampaignAngleId {
  return typeof value === 'string' && Object.hasOwn(CAMPAIGN_ANGLES, value);
}

export function campaignAngle(id: unknown): CampaignAngle | null {
  return isCampaignAngleId(id) ? CAMPAIGN_ANGLES[id] : null;
}
